/**
 * briefLogic - pure derivations behind the AI brief surfaces (docs/plans/crm-brief-plan.md B6).
 * The server sends data; every recruiter-facing word is rendered here. No api import (and no lib.ts
 * import, which reaches the axios client), so the tests need no mock.
 */
import type { Brief, BriefNextKind } from './types';

export type BriefTone = 'red' | 'amber' | 'neutral';

/** Urgency at or above which a brief reads red / amber. */
export const BRIEF_RED_AT = 85;
export const BRIEF_AMBER_AT = 60;

/** Re-brief button lock after a click, in milliseconds (the server limits to one per 60 s). */
export const BRIEF_REFRESH_COOLDOWN_MS = 60_000;

const NEXT_KIND_LABEL: Record<BriefNextKind, string> = {
  call: 'Call',
  text: 'Text',
  email: 'Email',
  book: 'Book',
  review: 'Review',
  wait: 'Wait',
};

/** "Call" | "Text" | "Email" | "Book" | "Review" | "Wait" (unknown kinds read "Review"). */
export function nextKindLabel(kind: BriefNextKind | string | null | undefined): string {
  return NEXT_KIND_LABEL[kind as BriefNextKind] ?? 'Review';
}

/** red >= 85, amber >= 60, else neutral. A missing or non-numeric urgency is neutral. */
export function briefTone(urgency: number | null | undefined): BriefTone {
  if (typeof urgency !== 'number' || Number.isNaN(urgency)) return 'neutral';
  if (urgency >= BRIEF_RED_AT) return 'red';
  if (urgency >= BRIEF_AMBER_AT) return 'amber';
  return 'neutral';
}

const parse = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const sameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "3:00 pm" in the viewer's local time. */
export function clockLabel(d: Date): string {
  const h = d.getHours();
  const m = d.getMinutes();
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
}

/** "due 3:00 pm" when the brief's due time is today (local), else null. */
export function briefDueToday(brief: Pick<Brief, 'due_at'> | null | undefined, now: Date = new Date()): string | null {
  const ms = parse(brief?.due_at);
  if (ms == null) return null;
  const due = new Date(ms);
  return sameLocalDay(due, now) ? `due ${clockLabel(due)}` : null;
}

/**
 * The one-line reason: the brief's why-line, with " · due <time>" appended when its due time is
 * today. Empty string when there is no brief (callers fall back to the rule-based label).
 */
export function briefReasonLabel(brief: Pick<Brief, 'why' | 'due_at'> | null | undefined, now: Date = new Date()): string {
  const why = (brief?.why ?? '').trim();
  if (!why) return '';
  const due = briefDueToday(brief, now);
  return due ? `${why} · ${due}` : why;
}

/** "just now", "2 min ago", "3 h ago", "4 d ago" from an ISO time; '' when unknown. */
export function briefAgeLabel(
  brief: Pick<Brief, 'updated_at' | 'as_of'> | null | undefined,
  now: Date = new Date(),
): string {
  const ms = parse(brief?.updated_at ?? brief?.as_of);
  if (ms == null) return '';
  const min = Math.floor(Math.max(0, now.getTime() - ms) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}
